import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, TextField, Button, FormControl, RadioGroup, FormControlLabel, Radio, Modal, IconButton } from '@mui/material';
import ctd from 'country-telephone-data';
import ChevronRight from '../../components/ChevronRight';
import AlertTriangle from '../../assets/AlertTriangle.svg';
import CheckIcon from '@mui/icons-material/Check';
import { pageBoxSx, pageTitleSx, headerRowSx, leftTextSx, sectionPaperSx, formInputSx, labelBoldSx, addAccountSX, dispatcherSx, supervisorSx, saveButtonSx, cancelButtonSx } from './commandCentre.styles';
import { createCommandUser, updateCommandUser } from '../../services/commandCentreService';
import { sectionTitleSx } from '../../styles/sectionTokens';

const CreateAccount: React.FC<{ initial?: any; onCancel?: () => void; onSave?: () => void; onDeactivate?: (name: string) => void }> = ({ initial, onCancel, onSave, onDeactivate }) => {
  const [fullName, setFullName] = useState(initial ? `${initial.firstName} ${initial.lastName}` : '');
  const [email, setEmail] = useState(initial?.email ?? '');
  const initialPhone = initial?.phone ?? '';
  const [countryCode, setCountryCode] = useState<string>(() => {
    const match = String(initialPhone).match(/^(\+\d{1,4})/);
    return match ? match[1] : '+91';
  });
  const [phone, setPhone] = useState(initialPhone.replace(/^(\+\d{1,4})\s?/, '') ?? '');
  const [role, setRole] = useState<'Dispatcher' | 'Supervisor'>(initial?.role ?? 'Dispatcher');
  const [status, setStatus] = useState<'Active' | 'Inactive'>(initial?.status ?? 'Active');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const openConfirm = () => setConfirmOpen(true);
  const closeConfirm = () => setConfirmOpen(false);
  const confirmDeactivate = () => {
    (async () => {
      try {
        if (initial && (initial.user_id || initial.id)) {
          const userId = initial.user_id ?? initial.id;
          const resp = await (await import('../../services/commandCentreService')).deactivateCommandUser(userId);
          const ok = resp?.success ?? resp?.data?.success ?? true;
          if (ok) {
            window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: 'Account deactivated' } }));
            if (onDeactivate) onDeactivate(fullName);
          } else {
            window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: 'Failed to deactivate account' } }));
          }
        } else {
          if (onDeactivate) onDeactivate(fullName);
        }
      } catch (err: any) {
        window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: err?.message ?? 'Failed to deactivate account' } }));
      } finally {
        closeConfirm();
      }
    })();
  };

  const deactivateSx = {
  width: '153.59375px',
  height: '41.5px',
  opacity: 1,
  borderRadius: '10px',
  border: '1px solid #FCA5A5',
  textTransform: 'none',
  backgroundColor: 'transparent',
  color: '#DE606C',
  fontFamily: 'Inter, Arial, sans-serif',
  fontWeight: 500,
  fontSize: '13px',
  lineHeight: '19.5px',
  letterSpacing: '-0.08px',
  textAlign: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' }
  } as any;

  useEffect(() => {
    return () => {};
  }, []);

  const isEmailValid = (e: string) => {
    if (!e) return false;
    // basic email check
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());
  };

  const isFormValid = () => {
    if (!fullName || !String(fullName).trim()) return false;
    if (!email || !isEmailValid(email)) return false;
    if (!role) return false;
    return true;
  };

  const handleSave = async () => {
    if (!isFormValid()) {
      setFormError('Please fill required fields correctly');
      return;
    }
    setSaving(true);
    // build minimal payload: include only fields that changed when editing
    const nameVal = String(fullName).trim();
    const phoneVal = phone ? `${countryCode} ${String(phone).trim()}` : '';
    const emailVal = String(email).trim();
    const roleVal = role === 'Supervisor' ? ('SUPERVISOR' as const) : ('DISPATCHER' as const);

    try {
      if (initial && (initial.user_id || initial.id)) {
        const patch: any = {};
        if (nameVal && nameVal !== ((initial.name ?? `${initial.firstName ?? ''} ${initial.lastName ?? ''}`).trim())) patch.name = nameVal;
        if (phoneVal && phoneVal !== (initial.phone ?? '')) patch.phone = phoneVal;
        if (emailVal && emailVal !== (initial.email ?? '')) patch.email = emailVal;
        if (roleVal && ((initial.user_role ?? initial.role ?? '').toString().toUpperCase() !== roleVal)) patch.role = roleVal;

        if (Object.keys(patch).length === 0) {
          window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: 'No changes to save' } }));
        } else {
          const userId = initial.user_id ?? initial.id;
          const body = await updateCommandUser(userId, patch);
          if (body?.success === false) {
            setFormError(body?.message ?? 'Failed to update account');
          } else {
            window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: body?.message ?? 'Account updated successfully' } }));
            if (onSave) onSave();
          }
        }
      } else {
        const payload = { name: nameVal, phone: phoneVal, email: emailVal, role: roleVal };
        const body = await createCommandUser(payload);
        if (body?.success === false) {
          setFormError(body?.message ?? 'Failed to create account');
        } else {
          window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: body?.message ?? 'Account created successfully' } }));
          if (onSave) onSave();
        }
      }
    } catch (err: any) {
      setFormError(err?.message ?? 'Network error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={pageBoxSx}>
      <Box mb={2} sx={{ position: 'relative' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button size="small" onClick={() => onCancel && onCancel()} sx={{ textTransform: 'none', p: 0, minWidth: 0, color: '#6B7280', fontSize: '13px' }}>Central Command</Button>
            <Typography sx={{ color: '#6B7280' }}><ChevronRight /></Typography>
            <Typography sx={{ fontSize: 13, color: '#6B7280' }}>{initial ? `${initial.firstName} ${initial.lastName}` : 'Create account'}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={leftTextSx}>
              <Typography sx={pageTitleSx}>{initial ? `${initial.firstName} ${initial.lastName}` : 'Create Account'}</Typography>
            </Box>
          </Box>
        </Box>
        {formError && (
          <Box sx={{ position: 'absolute', right: 0, top: 0, width: '356px', height: '54px', borderRadius: '8px', backgroundColor: '#FEF2F2', border: '1px solid #FCA5A5', display: 'flex', alignItems: 'center', px: 2, zIndex: 20 }}>
            <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#DE606C', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, flexShrink: 0 }}>
              <svg width="10" height="10" viewBox="0 0 8 8" fill="none"><path d="M1 1L7 7M7 1L1 7" stroke="#FFFFFF" strokeWidth="1.2" strokeLinecap="round" /></svg>
            </Box>
            <Typography sx={{ flex: 1, fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px', lineHeight: '19.5px', letterSpacing: '-0.08px', color: '#374151' }}>{formError}</Typography>
            <Box component="span" onClick={() => setFormError(null)} sx={{ cursor: 'pointer', color: '#9CA3AF', fontSize: 18, lineHeight: 1, ml: 1 }}>×</Box>
          </Box>
        )}
      </Box>

      <Paper sx={{ ...(sectionPaperSx as any), width: '720px', mb: 3, 	height:'20rem', }}>
        <Typography sx={{ fontWeight: 700, mb: 1, fontSize: '0.9rem' }}>Account Details</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography sx={labelBoldSx}>Full name *</Typography>
            <TextField fullWidth size="small" placeholder="e.g. Sarah Chen" value={fullName} onChange={(e) => setFullName(e.target.value)} sx={{ ...formInputSx, mt: 1 }} />
          </Box>
          <Box>
            <Typography sx={labelBoldSx}>Email address *</Typography>
            <TextField fullWidth size="small" placeholder="name@cmd.Wualt.co" value={email} onChange={(e) => setEmail(e.target.value)} sx={{ ...formInputSx, mt: 1 }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px', lineHeight: '20px', color: '#374151' }}>Phone (optional)</Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
              <FormControl size="small" sx={{ width: '180px' }}>
                <select value={countryCode} onChange={(e) => setCountryCode(e.target.value)} style={{ height: '41.5px', borderRadius: '10px', paddingLeft: '12px', border: '1px solid #E5E7EB', background: 'white' }}>
                  {ctd.allCountries.map((c: any) => {
                    const rawName = c.name ?? c[0] ?? String(c);
                    const cleanName = String(rawName).replace(/\s*\(.*\)\s*$/, '');
                    return (
                      <option key={c.iso2 ?? cleanName} value={c.dialCode ? `+${c.dialCode}` : ''}>{`${cleanName} (+${c.dialCode})`}</option>
                    );
                  })}
                </select>
              </FormControl>
              <TextField fullWidth size="small" placeholder="7700 900000" value={phone} onChange={(e) => setPhone(e.target.value)} sx={{ ...formInputSx }} />
            </Box>
          </Box>
        </Box>
      </Paper>

  <Paper sx={{ ...(sectionPaperSx as any), width: '720px', mb: 3,  height:'9rem', }}>
        <Typography sx={{ fontWeight: 700, mb: 1, fontSize: '0.9rem' }}>Role</Typography>
        <Box sx={{ display: 'flex', gap: 3 }}>
          <Box sx={{ width: 325 }}>
            <Button onClick={() => setRole('Dispatcher')} sx={role === 'Dispatcher' ? { width: 325, height: 72, justifyContent: 'space-between', borderRadius: '14px', border: '1px solid #39A9B1', padding: '13px', textTransform: 'none', display: 'flex', alignItems: 'flex-start' } : { width: 325, height: 72, justifyContent: 'flex-start', textTransform: 'none', borderRadius: '14px', border: '1px solid #E5E7EB', padding: '13px', display: 'flex', alignItems: 'flex-start' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>Dispatcher</Typography>
                <Typography sx={{ fontSize: 12, color: '#9CA3AF' }}>Manages real-time incident response.</Typography>
              </Box>
              {role === 'Dispatcher' && (
                <Box sx={{ width: 16, height: 16, borderRadius: '9999px', backgroundColor: '#39A9B1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CheckIcon sx={{ width: 9, height: 9, color: '#FFFFFF' }} />
                </Box>
              )}
            </Button>
          </Box>

          <Box sx={{ width: 325 }}>
            <Button onClick={() => setRole('Supervisor')} sx={role === 'Supervisor' ? { width: 325, height: 72, justifyContent: 'space-between', borderRadius: '14px', border: '1px solid #39A9B1', padding: '13px', textTransform: 'none', display: 'flex', alignItems: 'flex-start' } : { width: 325, height: 72, justifyContent: 'flex-start', textTransform: 'none', borderRadius: '14px', border: '1px solid #E5E7EB', padding: '13px', display: 'flex', alignItems: 'flex-start' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>Supervisor</Typography>
                <Typography sx={{ fontSize: 12, color: '#9CA3AF' }}>Oversees dispatchers and operations.</Typography>
              </Box>
              {role === 'Supervisor' && (
                <Box sx={{ width: 16, height: 16, borderRadius: '9999px', backgroundColor: '#39A9B1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CheckIcon sx={{ width: 9, height: 9, color: '#FFFFFF' }} />
                </Box>
              )}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ width: '720px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {initial ? (
          <Box>
            <Button sx={deactivateSx} onClick={openConfirm}>
              Deactivate account
            </Button>
          </Box>
        ) : (
          <Box />
        )}

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button sx={saveButtonSx} onClick={handleSave} disabled={!isFormValid() || saving}>{saving ? 'Saving...' : 'Save'}</Button>
          <Button sx={cancelButtonSx} onClick={() => onCancel && onCancel()}>Cancel</Button>
        </Box>
      </Box>

      {/* Confirm Deactivate Modal */}
      <Modal open={confirmOpen} onClose={closeConfirm}>
        <Box sx={{ position: 'absolute', width: '400px', height: '229.6953125px', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', borderRadius: '16px', backgroundColor: '#FFFFFF', boxShadow: '0 10px 30px rgba(0,0,0,0.12)', outline: 'none', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ width: '100%', height: '65px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(0,0,0,0.06)', px: '24px' }}>
            <Typography sx={{ fontWeight: 600, fontSize: '16px' }}>Deactivate account?</Typography>
            <IconButton size="small" onClick={closeConfirm} sx={{ width: '13px', height: '13px', padding: 0 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </IconButton>
          </Box>
          <Box sx={{ width: '100%', height: '164.6953125px', gap: '20px', px: '24px', pt: '20px', pb: '20px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <Box sx={{ width: '352px', height: '67.1953125px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <Box sx={{ width: '60px', height: '36px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', backgroundColor: '#FEF2F2' }}>
                  <Box component="img" src={AlertTriangle} sx={{ width: '16px', height: '16px', opacity: 1, display: 'block', objectFit: 'contain' }} />
              </Box>
              <Box>
                <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '22.4px', color: 'text.primary' }}>
                  {fullName ? `"${fullName}" will lose access to the Command Dashboard immediately.` : 'This account will lose access to the Command Dashboard immediately.'}
                </Typography>
              </Box>
            </Box>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button sx={{ width: '76.4921875px', height: '37.5px', borderRadius: '10px', backgroundColor: '#E5E7EB', color: '#111827', textTransform: 'none', border: '1px solid rgba(0,0,0,0.08)' }} onClick={closeConfirm}>Cancel</Button>
              <Button sx={{ width: '98.21875px', height: '35.5px', borderRadius: '10px', backgroundColor: '#DE606C', color: '#FFFFFF', textTransform: 'none' }} onClick={confirmDeactivate}>Deactivate</Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default CreateAccount;
